import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { loadDraftFromStorage, clearDraftFromStorage, loadDraftFromDatabase } from '../useClaimState';
import api from '@/utils/api';
import type { ClaimState, ClaimDispatch, PhaseId } from '../types';

interface UseDraftManagerParams {
  resumeCaseId: string | undefined;
  state: ClaimState;
  dispatch: ClaimDispatch;
  setCaseId: (caseId: string, caseNumber: string) => void;
  goToPhase: (phase: PhaseId) => void;
  navigate: (options: any) => void;
}

interface UseDraftManagerReturn {
  hasInitialized: React.MutableRefObject<boolean>;
  hasLoadedDraft: React.MutableRefObject<boolean>;
}

/**
 * Draft manager hook for new claim flow.
 * Handles loading drafts from localStorage and database.
 */
export function useDraftManager({
  resumeCaseId,
  state,
  dispatch,
  setCaseId,
  goToPhase,
  navigate,
}: UseDraftManagerParams): UseDraftManagerReturn {
  const hasInitialized = useRef(false);
  const hasLoadedDraft = useRef(false);

  // Auto-recover from localStorage on mount (when no resumeCase URL param)
  useEffect(() => {
    const recoverDraft = async () => {
      // Skip if we have a resumeCase URL param (database recovery takes priority)
      // Skip if already loaded or initialized
      if (resumeCaseId || hasLoadedDraft.current || hasInitialized.current) {
        return;
      }

      const localDraft = loadDraftFromStorage();

      // Recover if we have meaningful data (segments) or if awaiting email
      const hasSegments = localDraft?.editableSegments && localDraft.editableSegments.length > 0;
      const isAwaitingEmail = localDraft?.phase === 'awaiting_email' && localDraft?.inboundEmailAddress;

      if (localDraft && (hasSegments || isAwaitingEmail)) {
        hasLoadedDraft.current = true;
        hasInitialized.current = true;

        // If draft has a caseId, verify it still exists in the database
        let validCaseId = localDraft.caseId;
        let validCaseNumber = localDraft.caseNumber;

        if (localDraft.caseId) {
          try {
            await api.get(`/api/cases/${localDraft.caseId}/`);
            // Case exists, use it
          } catch (err: any) {
            if (err.response?.status === 404) {
              // Case was deleted, clear the ID
              validCaseId = null;
              validCaseNumber = null;
              // Clear stale draft from storage so we don't keep trying
              clearDraftFromStorage();
              toast.warning('Previous case was deleted. Your data has been preserved - a new case will be created.');
            }
            // For other errors, keep the caseId and let normal error handling catch it later
          }
        }

        // Restore state from localStorage with validated caseId
        if (validCaseId && validCaseNumber) {
          setCaseId(validCaseId, validCaseNumber);
        }
        if (localDraft.editableSegments && localDraft.editableSegments.length > 0) {
          dispatch({ type: 'UPDATE_SEGMENTS', segments: localDraft.editableSegments });
        }

        if (localDraft.passengers && localDraft.passengers.length > 0) {
          dispatch({ type: 'UPDATE_PASSENGERS', passengers: localDraft.passengers });
        }
        if (localDraft.pnr) {
          dispatch({ type: 'SET_PNR', pnr: localDraft.pnr });
        }
        if (localDraft.incidentType) {
          dispatch({ type: 'SET_INCIDENT_TYPE', incidentType: localDraft.incidentType });
        }
        if (localDraft.affectedSegmentId) {
          dispatch({ type: 'SET_AFFECTED_SEGMENT', segmentId: localDraft.affectedSegmentId });
        }
        if (localDraft.incidentDescription) {
          dispatch({ type: 'SET_INCIDENT_DESCRIPTION', description: localDraft.incidentDescription });
        }
        if (localDraft.narrative) {
          dispatch({ type: 'SET_NARRATIVE', narrative: localDraft.narrative });
        }
        // Restore expenses (one at a time since there's no bulk action)
        if (localDraft.expenses && localDraft.expenses.length > 0) {
          for (const expense of localDraft.expenses) {
            dispatch({ type: 'ADD_EXPENSE', expense });
          }
        }
        // Restore inbound email address for awaiting_email phase
        if (localDraft.inboundEmailAddress) {
          dispatch({ type: 'SET_INBOUND_EMAIL_ADDRESS', emailAddress: localDraft.inboundEmailAddress });
        }

        // Navigate to the saved phase (default to booking_review if not set)
        const targetPhase = localDraft.phase || 'booking_review';
        goToPhase(targetPhase);

        // Show appropriate message based on whether we had to clear the caseId
        if (localDraft.caseId && !validCaseId) {
          // Already showed warning toast above
        } else {
          toast.info('Draft recovered');
        }
      }
    };

    recoverDraft();
  }, [resumeCaseId, setCaseId, dispatch, goToPhase]);

  // Load draft from database if resumeCaseId is provided
  useEffect(() => {
    const loadDraft = async () => {
      if (resumeCaseId && !hasLoadedDraft.current) {
        hasLoadedDraft.current = true;
        try {
          const draft = await loadDraftFromDatabase(resumeCaseId);
          if (draft) {
            // Restore state from database
            if (draft.caseId && draft.caseNumber) {
              setCaseId(draft.caseId, draft.caseNumber);
            }
            if (draft.editableSegments && draft.editableSegments.length > 0) {
              dispatch({ type: 'UPDATE_SEGMENTS', segments: draft.editableSegments });
            }
            if (draft.passengers && draft.passengers.length > 0) {
              dispatch({ type: 'UPDATE_PASSENGERS', passengers: draft.passengers });
            }
            if (draft.pnr) {
              dispatch({ type: 'SET_PNR', pnr: draft.pnr });
            }
            if (draft.incidentType) {
              dispatch({ type: 'SET_INCIDENT_TYPE', incidentType: draft.incidentType });
            }
            if (draft.affectedSegmentId) {
              dispatch({ type: 'SET_AFFECTED_SEGMENT', segmentId: draft.affectedSegmentId });
            }
            if (draft.incidentDescription) {
              dispatch({ type: 'SET_INCIDENT_DESCRIPTION', description: draft.incidentDescription });
            }
            if (draft.narrative) {
              dispatch({ type: 'SET_NARRATIVE', narrative: draft.narrative });
            }
            // Restore inbound email address for awaiting_email phase
            if (draft.inboundEmailAddress) {
              dispatch({ type: 'SET_INBOUND_EMAIL_ADDRESS', emailAddress: draft.inboundEmailAddress });
            }
            // Navigate to the saved phase
            if (draft.phase) {
              goToPhase(draft.phase);
            }
            hasInitialized.current = true;
            toast.success('Draft loaded successfully');
          }
        } catch (err: any) {
          if (err.response?.status === 404) {
            // Case was deleted, redirect to start fresh
            toast.error('The case you are trying to resume no longer exists.');
            navigate({ to: '/client/new-claim', replace: true });
          } else {
            console.error('Failed to load draft:', err);
          }
        }
      }
    };
    loadDraft();
  }, [resumeCaseId, setCaseId, dispatch, goToPhase, navigate]);

  return {
    hasInitialized,
    hasLoadedDraft,
  };
}
